// Interactive stations: the helm, engine throttle, SG radar scope, Mk 8
// rangekeeper in plot, the Mk 38 director with its stereoscopic rangefinder
// minigame, turret II local control, and the DC status board. Walk up,
// press E, and the station takes the keyboard; E or ESC gives it back.

import * as THREE from "three";
import { KTS } from "./const.js";
import { solveElevation, MAX_RANGE } from "./guns.js";

const wrap = (a) => Math.atan2(Math.sin(a), Math.cos(a));

// Touch consoles: every station key becomes a labeled button on glass, wired
// straight into the SAME onKey/keysDown paths the keyboard uses — the button
// row is a keyboard that happens to be drawn. Modes: plain = one press,
// "rep" = press-and-repeat (a held rudder key), "hold" = the code sits in
// player.keys while the finger is down (the plot's cranks, the rangefinder
// knob). The trailing ✕ (KeyE) secures the station, same as everywhere.
const TOUCH_BTNS = {
  helm: [["KeyA", "◀ RUDDER", "rep"], ["KeyQ", "MIDSHIPS"], ["KeyD", "RUDDER ▶", "rep"],
    ["KeyS", "BELL −", "rep"], ["KeyW", "BELL +", "rep"], ["KeyF", "FIRE", "", 1]],
  throttle: [["KeyW", "WORK THE THROTTLES", "rep"]],
  radar: [["KeyT", "NEXT CONTACT"], ["BracketLeft", "SCALE −"], ["BracketRight", "SCALE +"],
    ["KeyF", "DESIGNATE", "", 1]],
  plot: [["KeyA", "◀ CRS", "hold"], ["KeyD", "CRS ▶", "hold"], ["KeyS", "SPD −", "hold"],
    ["KeyW", "SPD +", "hold"], ["KeyX", "ZERO"], ["KeyR", "RAPID"], ["KeyY", "RADAR"],
    ["KeyF", "SALVO", "", 1]],
  director: [["ArrowLeft", "◀ KNOB", "hold"], ["ArrowRight", "KNOB ▶", "hold"],
    ["KeyT", "DESIGNATE"], ["Space", "MARK CUT"], ["KeyF", "FIRE", "", 1]],
  directorSpot: [["KeyJ", "DROP 100", "rep"], ["KeyU", "ADD 100", "rep"],
    ["KeyH", "LEFT 100", "rep"], ["KeyK", "RIGHT 100", "rep"], ["KeyN", "NO CHANGE"],
    ["KeyC", "SEND THE SPOT", "", 1]],
  turret: [["KeyL", "LOCAL"], ["KeyA", "◀ TRAIN", "hold"], ["KeyD", "TRAIN ▶", "hold"],
    ["KeyS", "ELEV −", "hold"], ["KeyW", "ELEV +", "hold"], ["KeyF", "SHOOT", "", 1]],
  dc: [["KeyM", "FLOOD MAGAZINE", "", 1], ["KeyC", "CTR-FLOOD +"], ["KeyV", "CTR-FLOOD −"]],
};

const TELEGRAPH = [
  { name: "ALL STOP", kts: 0 }, { name: "AHEAD 1/3", kts: 5 }, { name: "AHEAD 2/3", kts: 10 },
  { name: "AHEAD STANDARD", kts: 15 }, { name: "AHEAD FULL", kts: 22 }, { name: "AHEAD FLANK", kts: 32 },
];

export class Stations {
  constructor(G) {
    this.G = G;
    this.active = null;      // station def while manned
    this.el = document.getElementById("bb-station");
    this.uiClock = 0;
    this.rf = { knob: 0, hidden: 0, drift: 0 };  // rangefinder minigame state
    this.telegraphIdx = 2;
    this.viewYaw = 0;        // director free view (ship-relative)
    this.dirZoom = 12;
    this.eyeLocal = null;    // camera override (turret periscope head)
    this._eyeV = new THREE.Vector3();

    // Touch button wiring, by delegation on the (persistent) overlay — the
    // panel's innerHTML is rebuilt 8×/s, so per-button listeners would die
    // between pointerdown and pointerup. Each held code remembers WHICH
    // finger holds it (code -> pointerId): only that finger's up releases
    // it, so two cranks turn at once (train + elevate) and a look drag
    // lifting off the glass doesn't drop the crank in your other hand. The
    // window-level up still catches strays through re-renders — a re-render
    // must never leave a crank phantom-held.
    this.heldCodes = new Map();
    this._repTimers = new Map(); // code -> {delay, timer}, one repeater per crank
    this.el.addEventListener("pointerdown", (e) => {
      const b = e.target.closest("[data-code]");
      if (!b) return;
      e.preventDefault();
      const code = b.dataset.code, mode = b.dataset.mode;
      if (mode === "hold") { this.heldCodes.set(code, e.pointerId); G.player.keys.add(code); }
      else if (mode === "rep") {
        this.heldCodes.set(code, e.pointerId);
        this.onKey(code);
        this.stopRepeat(code);
        const rep = { timer: null };
        rep.delay = setTimeout(() => {
          rep.timer = setInterval(() => { if (this.heldCodes.has(code)) this.onKey(code); }, 150);
        }, 320);
        this._repTimers.set(code, rep);
      } else this.onKey(code);
    });
    const release = (e) => {
      for (const [c, pid] of this.heldCodes) {
        if (pid !== e.pointerId) continue;
        G.player.keys.delete(c);
        this.heldCodes.delete(c);
        this.stopRepeat(c);
      }
    };
    window.addEventListener("pointerup", release);
    window.addEventListener("pointercancel", release);
  }

  stopRepeat(code) {
    const rep = this._repTimers.get(code);
    if (!rep) return;
    clearTimeout(rep.delay); clearInterval(rep.timer);
    this._repTimers.delete(code);
  }

  releaseHeld() {
    for (const c of this.heldCodes.keys()) {
      this.G.player.keys.delete(c);
      this.stopRepeat(c);
    }
    this.heldCodes.clear();
  }

  nearest() {
    const p = this.G.player.pos;
    let best = null, bd = 2.6;
    for (const s of this.G.ship.stations) {
      const d = Math.hypot(s.pos.x - p.x, s.pos.z - p.z) + Math.abs(s.pos.y - p.y) * 0.7;
      if (d < bd) { bd = d; best = s; }
    }
    return best;
  }

  enter(st) {
    const G = this.G;
    this.active = st;
    G.hud.setPrompt(null); // don't leave "TAP — MAN ..." hanging under the console
    G.player.frozen = true;
    G.player.pos.set(st.pos.x, st.pos.y + 0.01, st.pos.z);
    G.player.yaw = st.yaw;
    G.player.pitch = 0;
    G.player.zoom = 1;
    this.el.classList.remove("hidden");
    if (st.kind === "director") {
      const fc = G.fc;
      this.viewYaw = fc.target ? wrap(fc.targetBearing() - G.shipState.heading) : 0;
      this.rf.hidden = (Math.random() - 0.5) * 60;
      this.rf.knob = 0;
      G.fc.directorManned = true;
    }
    if (st.kind === "plot") {
      G.fc.plotManned = true;
      G.player.pitch = -0.12; // eyes on the plotting board across the table
    }
    if (st.kind === "plot" || st.kind === "helm") G.audio.bosunPipe();
    this.render(true);
  }

  exit() {
    const G = this.G;
    if (!this.active) return;
    // a finger still parked on a crank must not follow the sailor out of
    // the booth as a phantom-held movement key
    this.releaseHeld();
    if (this.active.kind === "director") G.fc.directorManned = false;
    if (this.active.kind === "plot") G.fc.plotManned = false;
    if (this.active.kind === "turret") {
      for (const t of G.ship.turrets) for (const b of t.barrels) b.visible = true;
    }
    this.eyeLocal = null;
    this.active = null;
    G.player.frozen = false;
    G.player.zoom = 1;
    G.player.pitch = 0;
    this.el.classList.add("hidden");
    this.el.innerHTML = "";
  }

  // main.js forwards keydowns here first; return true if consumed
  onKey(code) {
    const G = this.G, st = this.active;
    if (!st) return false;
    if (code === "KeyE" || code === "Escape") { this.exit(); return true; }
    switch (st.kind) {
      case "helm":
        if (code === "KeyA") { G.shipState.orderedRudder = Math.max(-0.61, G.shipState.orderedRudder - 0.0873); return true; }
        if (code === "KeyD") { G.shipState.orderedRudder = Math.min(0.61, G.shipState.orderedRudder + 0.0873); return true; }
        if (code === "KeyQ") { G.shipState.orderedRudder = 0; return true; }
        if (code === "KeyW") { this.telegraphIdx = Math.min(TELEGRAPH.length - 1, this.telegraphIdx + 1); this.telegraph(); return true; }
        if (code === "KeyS") { this.telegraphIdx = Math.max(0, this.telegraphIdx - 1); this.telegraph(); return true; }
        if (code === "KeyF") { G.fc.fire(); return true; }
        break;
      case "throttle":
        if (code === "KeyW") { G.shipState.engineNudge = 1; return true; }
        if (code === "KeyS") { G.shipState.engineNudge = -1; return true; }
        break;
      case "radar": {
        if (code === "Tab" || code === "KeyT") { G.radar.cycle(); return true; }
        if (code === "Enter" || code === "KeyF") {
          const c = G.radar.current();
          if (c) G.fc.designate(c.enemy, "radar");
          else G.hud.log("CIC: NO CONTACTS HELD", "bad");
          return true;
        }
        if (code === "BracketLeft") { G.radar.scale = Math.max(10000, G.radar.scale - 10000); return true; }
        if (code === "BracketRight") { G.radar.scale = Math.min(40000, G.radar.scale + 10000); return true; }
        break;
      }
      case "plot":
        if (code === "KeyF") { G.fc.fire(); return true; }
        if (code === "KeyX") { G.fc.zeroPlot(); return true; }
        if (code === "KeyR") { G.fc.rapidFire = !G.fc.rapidFire; G.hud.log(`PLOT: ${G.fc.rapidFire ? "RAPID CONTINUOUS FIRE" : "SALVO FIRE ON COMMAND"}`); return true; }
        if (code === "KeyY") { G.fc.radarRanging = !G.fc.radarRanging; G.hud.log(`PLOT: RADAR RANGING ${G.fc.radarRanging ? "IN" : "OUT"}`); return true; }
        if (code === "Digit1" || code === "Digit2" || code === "Digit3") { G.fc.fire({ only: +code.slice(-1) - 1 }); return true; }
        break;
      case "director":
        // a pending spot call owns the spotting keys (deflection then range,
        // in the traditional order: RIGHT 100... ADD 400... and send)
        if (G.fc.pendingSpot) {
          if (code === "KeyU") { G.fc.adjustSpot(100, 0); return true; }
          if (code === "KeyJ") { G.fc.adjustSpot(-100, 0); return true; }
          if (code === "KeyH") { G.fc.adjustSpot(0, -100); return true; }
          if (code === "KeyK") { G.fc.adjustSpot(0, 100); return true; }
          if (code === "KeyC") { G.fc.applyPlayerSpot(); return true; }
          if (code === "KeyN") { G.fc.pendingSpot.range = 0; G.fc.pendingSpot.defl = 0; G.fc.applyPlayerSpot(); return true; }
        }
        if (code === "KeyT") { this.designateFromDirector(); return true; }
        if (code === "Space") { this.rangeCut(); return true; }
        if (code === "KeyF") { G.fc.fire(); return true; }
        if (code === "KeyQ") { this.dirZoom = this.dirZoom === 12 ? 25 : 12; return true; }
        break;
      case "turret": {
        const t = G.ship.turrets[1];
        if (!t.localOrder) t.localOrder = { train: t.train, elev: t.elev };
        if (code === "KeyF") {
          if (G.guns.localTurret === 1 && G.guns.fireLocal(t)) {
            G.hud.log("TURRET II — LOCAL CONTROL SALVO");
          }
          return true;
        }
        if (code === "KeyL") {
          G.guns.localTurret = G.guns.localTurret === 1 ? null : 1;
          G.hud.log(G.guns.localTurret === 1 ? "TURRET II IN LOCAL CONTROL" : "TURRET II BACK IN PRIMARY (PLOT) CONTROL");
          return true;
        }
        break;
      }
      case "dc":
        if (code === "KeyM") { G.damage.floodMagazine(); return true; }
        if (code === "KeyC") { G.damage.counterflood = Math.min(3, G.damage.counterflood + 1); G.hud.log(`DC: COUNTERFLOODING GROUP ${G.damage.counterflood}`); return true; }
        if (code === "KeyV") { G.damage.counterflood = Math.max(0, G.damage.counterflood - 1); return true; }
        break;
    }
    return false;
  }

  telegraph() {
    const G = this.G;
    const t = TELEGRAPH[this.telegraphIdx];
    G.shipState.orderedSpeed = Math.min(t.kts, G.shipState.speedCap ?? 33) * KTS;
    G.audio.ding();
    G.hud.log(`BRIDGE: ${t.name}`);
  }

  designateFromDirector() {
    const G = this.G;
    const H = G.shipState.heading;
    const b = wrap(this.viewYaw + H);
    let best = null, bd = 0.06;
    const p = G.shipWorldPos();
    for (const e of G.targets()) {
      if (e.sunk) continue;
      const eb = Math.atan2(e.pos.x - p.x, e.pos.y - p.z);
      const d = Math.abs(wrap(eb - b));
      if (d < bd) { bd = d; best = e; }
    }
    if (best && G.night && !G.illum.isLit(best)) {
      G.hud.log("DIRECTOR: TOO DARK — GUN FLASHES ONLY. GET ILLUMINATION UP (I) OR DESIGNATE FROM CIC.", "bad");
      return;
    }
    if (best) G.fc.designate(best, "optical");
    else G.hud.log("DIRECTOR: NOTHING ON THE WIRE", "bad");
  }

  rangeCut() {
    const G = this.G;
    if (!G.fc.target) { G.hud.log("DIRECTOR: NO DESIGNATED TARGET", "bad"); return; }
    if (G.night && !G.illum.isLit(G.fc.target)) {
      G.hud.log("DIRECTOR: NO IMAGE — TARGET'S IN THE DARK. STAR SHELLS FIRST (I).", "bad");
      return;
    }
    const err01 = Math.min(1, Math.abs(this.rf.knob - this.rf.hidden) / 55);
    G.fc.rangeCut(err01);
    this.rf.hidden = (Math.random() - 0.5) * 70;
  }

  // per-frame while a station is manned
  update(dt, keysDown) {
    const G = this.G, st = this.active;
    if (!st) return;
    if (st.kind === "director") {
      // mouse-free slew with A/D, and the housing follows
      const slew = (keysDown.has("KeyD") ? 1 : 0) - (keysDown.has("KeyA") ? 1 : 0);
      this.viewYaw = wrap(this.viewYaw + slew * dt * 0.25);
      // track target automatically once designated (the pointers follow the
      // director) — unless a hand is on the glass: keyboard slew or a touch
      // drag (touch.js holds dragSlew while the finger is down) both take over
      const fc = G.fc;
      if (fc.target && !slew && !this.dragSlew) {
        const want = wrap(fc.targetBearing() - G.shipState.heading);
        const d = wrap(want - this.viewYaw);
        this.viewYaw = wrap(this.viewYaw + Math.max(-0.3 * dt, Math.min(0.3 * dt, d)));
        fc.directorOnTarget = Math.abs(d) < 0.02;
      } else fc.directorOnTarget = false;
      G.player.yaw = this.viewYaw;
      G.player.pitch = 0.003;
      G.player.zoom = this.dirZoom;
      G.ship.directorMesh.rotation.y = -this.viewYaw;
      G.ship.aftDirectorMesh.rotation.y = -this.viewYaw;
      // rangefinder knob
      const kd = (keysDown.has("ArrowRight") ? 1 : 0) - (keysDown.has("ArrowLeft") ? 1 : 0);
      this.rf.knob += kd * dt * 26;
      this.rf.hidden += Math.sin(performance.now() / 3400) * dt * 2.4; // target motion drifts the cut
    }
    if (st.kind === "plot") {
      // rate control: crank estimated target course (A/D) and speed (W/S)
      const dc = (keysDown.has("KeyD") ? 1 : 0) - (keysDown.has("KeyA") ? 1 : 0);
      const ds = (keysDown.has("KeyW") ? 1 : 0) - (keysDown.has("KeyS") ? 1 : 0);
      if (dc || ds) G.fc.adjustRates(dc * dt * 0.35, ds * dt * 2.6 * KTS);
    }
    if (st.kind === "turret") {
      const t = G.ship.turrets[1];
      if (G.guns.localTurret === 1) {
        if (!t.localOrder) t.localOrder = { train: t.train, elev: t.elev };
        const tr = (keysDown.has("KeyD") ? 1 : 0) - (keysDown.has("KeyA") ? 1 : 0);
        const el = (keysDown.has("KeyW") ? 1 : 0) - (keysDown.has("KeyS") ? 1 : 0);
        t.localOrder.train = wrap(t.localOrder.train + tr * dt * 0.12);
        t.localOrder.elev = Math.max(0, Math.min(0.785, t.localOrder.elev + el * dt * 0.1));
      }
      // periscope view: the eye rides the turret roof periscope, not the
      // booth — the booth's own view is a faceful of turret rear plate
      G.player.yaw = t.train;
      G.player.pitch = 0.02;
      G.player.zoom = 8;
      const v = this._eyeV.set(-4.5, 6.4, 2); // offset between the barrels, like the real hood

      t.group.localToWorld(v);
      G.shipInner.worldToLocal(v);
      this.eyeLocal = v;
      // The scope is focused on the horizon; at 8× the battery's own tubes —
      // this turret's a hand's breadth away, turret I's superfiring 30 m
      // ahead — render as walls of gray filling the frame (a long-range
      // solution buried the whole sight picture). Real optics would show
      // them as defocused smears the eye ignores, so the tubes drop out of
      // the scoped frame. The salvo camera (V) shows the ship from outside,
      // and they come back for the ride-along.
      const scoped = !G.shellCam?.on;
      for (const tt of G.ship.turrets) for (const b of tt.barrels) b.visible = !scoped;
    }
    if (st.kind === "throttle") {
      const n = G.shipState.engineNudge || 0;
      if (n) { G.shipState.engineManned = 3; G.shipState.engineNudge = 0; }
    }
    this.uiClock -= dt;
    if (this.uiClock <= 0) { this.uiClock = 0.12; this.render(); }
  }

  render(first = false) {
    const G = this.G, st = this.active;
    if (!st) return;
    const s = G.shipState, fc = G.fc;
    const deg = (r) => `${String(Math.round((((r * 180 / Math.PI) % 360) + 360) % 360)).padStart(3, "0")}°`;
    const kts = (v) => (v / KTS).toFixed(1);
    let html = `<div class="st-panel"><h3>${st.name}</h3>`;

    if (st.kind === "helm") {
      html += `<div class="st-row">
        <span class="dial"><span>COURSE</span><b>${deg(s.heading)}</b></span>
        <span class="dial"><span>SPEED</span><b>${kts(s.speed)} KTS</b></span>
        <span class="dial"><span>RUDDER</span><b>${(s.orderedRudder * 180 / Math.PI).toFixed(0)}°${s.rudderJammed ? " JAMMED" : ""}</b></span>
        <span class="dial"><span>TELEGRAPH</span><b>${TELEGRAPH[this.telegraphIdx].name}</b></span>
      </div>
      <div class="st-help"><b>A/D</b> rudder &nbsp; <b>Q</b> rudder amidships &nbsp; <b>W/S</b> engine telegraph &nbsp; <b>F</b> commence firing &nbsp; <b>E</b> lay below</div>`;
    }

    if (st.kind === "throttle") {
      const cap = (s.speedCap ?? 33);
      html += `<div class="st-row">
        <span class="dial"><span>ORDERED</span><b>${kts(s.orderedSpeed)} KTS</b></span>
        <span class="dial"><span>MAKING</span><b>${kts(s.speed)} KTS</b></span>
        <span class="dial"><span>MAX STEAM</span><b>${cap.toFixed(0)} KTS</b></span>
        <span class="dial"><span>SHAFTS</span><b>${Math.round(s.speed / KTS * 6.4)} RPM</b></span>
      </div>
      <div class="st-help">hold <b>W</b> to work the throttles — a manned throttle answers bells twice as fast. <b>E</b> to step away.</div>`;
    }

    if (st.kind === "radar") {
      const ls = G.radar.list();
      html += `<canvas id="st-scope" width="380" height="380"></canvas><div class="st-row">`;
      if (!ls.length) html += `<span>no surface contacts — no fire missions assigned</span>`;
      let ships = 0, tgts = 0;
      ls.forEach((c, i) => {
        const sel = i === Math.min(G.radar.selected, ls.length - 1);
        const tag = c.shore
          ? `TGT ${String.fromCharCode(65 + tgts++)} · ${c.enemy.name}`
          : `SKUNK ${String.fromCharCode(65 + ships++)}`;
        html += `<span style="${sel ? "color:var(--gold)" : ""}">${sel ? "▸" : ""}${tag} — ${deg(c.bearing)} / ${(c.range / 1000).toFixed(1)} km</span>`;
      });
      html += `</div><div class="st-help"><b>T</b> next contact / target &nbsp; <b>F</b> DESIGNATE to main battery &nbsp; <b>[ ]</b> range scale (${(G.radar.scale / 1000).toFixed(0)} km) &nbsp; <b>E</b> done</div>`;
    }

    if (st.kind === "plot") {
      const sol = fc.solution;
      html += `<canvas id="st-plotboard" width="640" height="340"></canvas>
      <div class="st-row">
        <span class="dial"><span>TARGET</span><b>${fc.target ? fc.target.name : "—"}</b></span>
        <span class="dial"><span>EST TGT COURSE</span><b>${fc.target ? deg(fc.est.course) : "—"}</b></span>
        <span class="dial"><span>EST TGT SPEED</span><b>${fc.target ? (fc.est.speed / KTS).toFixed(1) + " KTS" : "—"}</b></span>
        <span class="dial"><span>GEN. RANGE</span><b>${fc.target ? Math.round(fc.genRange).toLocaleString() + " M" : "—"}</b></span>
        <span class="dial"><span>T.O.F.</span><b>${sol ? Math.round(sol.tof) + " S" : "—"}</b></span>
      </div>
      <div class="st-row">
        <span class="dial"><span>RADAR RANGING</span><b>${fc.radarRanging && G.damage.radarAlive ? "IN" : "OUT"}</b></span>
        <span class="dial"><span>MODE</span><b>${fc.rapidFire ? "RAPID" : "SALVO"}</b></span>
        <span class="dial"><span>BATTERY</span><b>${G.guns.turrets.map((t) => t.disabled ? "✗" : t.ready ? "●" : t.blocked ? "arc" : "○").join(" ")}</b></span>
        <span class="dial"><span>SALVOS / STRADDLES / HITS</span><b>${fc.salvosFired} / ${fc.straddles} / ${fc.hits}</b></span>
      </div>
      <div class="st-help"><b>A/D</b> target course &nbsp; <b>W/S</b> target speed &nbsp; <b>X</b> zero plot &nbsp; <b>F</b> salvo &nbsp; <b>1/2/3</b> single turret &nbsp; <b>R</b> rapid &nbsp; <b>Y</b> radar ranging &nbsp; <b>E</b> secure</div>`;
    }

    if (st.kind === "director") {
      const ps = fc.pendingSpot;
      html += `<div id="st-rf-view">
        <canvas id="st-rf-canvas" width="480" height="130"></canvas>
        <div class="st-row" style="justify-content:center">
          <span class="dial"><span>DIRECTOR BEARING</span><b>${deg(this.viewYaw + s.heading)}</b></span>
          <span class="dial"><span>TARGET</span><b>${fc.target ? fc.target.name : "NONE — press T on a ship"}</b></span>
          <span class="dial"><span>MAGNIFICATION</span><b>${Math.round(this.dirZoom)}×</b></span>
          <span class="dial"><span>SOLUTION</span><b>${Math.round(fc.quality * 100)}%</b></span>
        </div>`;
      if (ps) {
        html += `<div class="st-row" style="justify-content:center">
          <span class="dial"><span>YOUR SPOT</span><b style="color:var(--gold)">${ps.defl ? (ps.defl > 0 ? "RIGHT " : "LEFT ") + Math.abs(ps.defl) + " · " : ""}${ps.range ? (ps.range > 0 ? "ADD " : "DROP ") + Math.abs(ps.range) : "NO CHANGE"}</b></span>
          <span class="dial"><span>WINDOW</span><b>${Math.ceil(ps.timer)} S</b></span>
        </div>
        <div class="st-help"><b>U/J</b> add / drop 100 &nbsp; <b>H/K</b> left / right 100 &nbsp; <b>N</b> no change &nbsp; <b>C</b> SEND THE SPOT</div>`;
      } else {
        html += `<div class="st-help"><b>mouse/A-D</b> train &nbsp; <b>T</b> designate &nbsp; <b>◀ ▶</b> rangefinder knob &nbsp; <b>SPACE</b> mark range cut &nbsp; <b>Q</b> power &nbsp; <b>F</b> fire &nbsp; <b>E</b> down ladder</div>`;
      }
      html += `</div>`;
    }

    if (st.kind === "turret") {
      const t = G.ship.turrets[1];
      const local = G.guns.localTurret === 1;
      const rng = t.localOrder ? this.elevToRange(t.localOrder.elev) : 0;
      html += `<div class="st-row">
        <span class="dial"><span>CONTROL</span><b>${t.disabled ? "OUT OF ACTION" : local ? "LOCAL" : "PRIMARY (PLOT)"}</b></span>
        <span class="dial"><span>TRAIN</span><b>${deg(t.train + s.heading)}</b></span>
        <span class="dial"><span>ELEVATION</span><b>${(t.elev * 180 / Math.PI).toFixed(1)}°</b></span>
        <span class="dial"><span>DROP AT</span><b>${local ? Math.round(rng).toLocaleString() + " M" : "PLOT'S CALL"}</b></span>
        <span class="dial"><span>GUNS</span><b>${t.blocked ? "MASKED — OUT OF ARC" : t.ready ? "READY" : G.guns.reload > 0 ? "LOADING " + Math.ceil(G.guns.reload) + "s" : "TRAINING"}</b></span>
      </div>
      ${t.blocked ? `<div class="st-help keep" style="color:var(--bad)">⚠ TARGET IS ABAFT THE TURRET'S STOPS — THE SHIP'S OWN STRUCTURE MASKS THE GUNS. COME ABOUT OR LET THE AFT TURRET TAKE IT.</div>` : ""}
      <div class="st-help"><b>L</b> take/yield local control &nbsp; <b>A/D</b> train &nbsp; <b>W/S</b> elevate &nbsp; <b>F</b> shoot &nbsp; <b>E</b> down the hatch</div>`;
    }

    if (st.kind === "dc") {
      const d = G.damage;
      html += `<div id="st-dc"><table><tr><th>COMPARTMENT</th><th>FIRE</th><th>FLOOD</th><th>PARTY</th></tr>`;
      for (const c of d.comps) {
        if (c.fire <= 0 && c.flood <= 0 && !c.party) continue;
        html += `<tr><td>${c.name}</td>
          <td><span class="dcbar"><i class="fire" style="width:${Math.min(100, c.fire * 100)}%"></i></span></td>
          <td><span class="dcbar"><i class="flood" style="width:${Math.min(100, c.flood * 100)}%"></i></span></td>
          <td>${c.party ? (c.party.working ? "REPAIR " + c.party.id + " ON IT" : "REPAIR " + c.party.id + " EN ROUTE") : "—"}</td></tr>`;
      }
      html += `</table>`;
      const anyC = d.comps.some((c) => c.fire > 0 || c.flood > 0);
      if (!anyC) html += `<div class="st-row" style="margin-top:6px"><span>All compartments dry. Material condition ${G.gq ? "ZED" : "YOKE"} set.</span></div>`;
      html += `<div class="st-row" style="margin-top:6px">
        <span class="dial"><span>FLOOD TOTAL</span><b>${d.floodTotal.toFixed(1)} / 6.0</b></span>
        <span class="dial"><span>LIST</span><b>${(s.list * 180 / Math.PI).toFixed(1)}°</b></span>
        <span class="dial"><span>MAGAZINE</span><b>${d.magazineFlooded ? "FLOODED" : d.magazineFuse !== null ? "FIRE! " + Math.ceil(d.magazineFuse) + "s" : "DRY"}</b></span>
        <span class="dial"><span>COUNTERFLOOD</span><b>GROUP ${d.counterflood}</b></span>
      </div>
      <div class="st-help"><b>M</b> flood 16-in magazine &nbsp; <b>C/V</b> counterflood more/less &nbsp; <b>E</b> carry on</div></div>`;
    }

    // On glass the key hints hide (CSS) and this row of real buttons takes
    // their place — same codes, same handlers, plus ✕ to secure.
    if (G.touch) {
      const btns = TOUCH_BTNS[st.kind === "director" && fc.pendingSpot ? "directorSpot" : st.kind] || [];
      html += `<div class="st-btnrow st-touchrow">` + btns.map(([c, t, mode, hot]) =>
        `<button class="st-btn${hot ? " hot" : ""}${this.heldCodes.has(c) ? " held" : ""}" data-code="${c}" data-mode="${mode || ""}">${t}</button>`
      ).join("") + `<button class="st-btn" data-code="KeyE">✕ SECURE</button></div>`;
    }

    html += "</div>";
    this.el.innerHTML = html;

    // canvas redraws
    if (st.kind === "radar") {
      const cv = document.getElementById("st-scope");
      if (cv) cv.getContext("2d").drawImage(G.radar.canvas, 0, 0, 380, 380);
    }
    if (st.kind === "plot") {
      const cv = document.getElementById("st-plotboard");
      if (cv) cv.getContext("2d").drawImage(G.plotBoard.canvas, 0, 0, 640, 340);
    }
    if (st.kind === "director") this.drawRangefinder();
  }

  // The Mk 8 radar's splash-spotting picture: target at the center hairline,
  // this salvo's splash pips at their range offsets (crisp with the radar in,
  // scattered without it), a thinner deflection strip below, and the gold
  // spot bug — key the spot until the bug sits ON the splash group, then
  // send it. That is the whole correction, exactly as the radar operators
  // described spotting 16-inch splashes "like drawing a picture on paper".
  drawSpotScope(ctx, W, H) {
    const fc = this.G.fc, ps = fc.pendingSpot;
    const SPAN = 1600; // meters of range offset across the scope
    const rx = (m) => W / 2 + (m / SPAN) * (W / 2 - 18);
    ctx.fillStyle = "#527257"; ctx.font = "10px monospace";
    ctx.textAlign = "left"; ctx.fillText("SPOT SCOPE — RANGE", 8, 13);
    ctx.textAlign = "right"; ctx.fillText("OVER →", W - 8, 13);
    ctx.textAlign = "left"; ctx.fillText("← SHORT", 8, 26);
    // range strip
    const y0 = 34, y1 = 78;
    ctx.strokeStyle = "#1d3a28";
    for (const m of [-1000, -500, 500, 1000]) {
      ctx.beginPath(); ctx.moveTo(rx(m), y0); ctx.lineTo(rx(m), y1); ctx.stroke();
    }
    // target hairline
    ctx.strokeStyle = "rgba(255,207,92,0.85)"; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(W / 2, y0 - 4); ctx.lineTo(W / 2, y1 + 4); ctx.stroke();
    ctx.lineWidth = 1;
    // splash pips
    ctx.fillStyle = "#8fe3a0";
    ps.pips.forEach((p, i) => {
      const y = y0 + 8 + (i % 3) * 13;
      ctx.beginPath(); ctx.ellipse(rx(Math.max(-SPAN, Math.min(SPAN, p))), y, 5, 3, 0, 0, Math.PI * 2); ctx.fill();
    });
    // the spot bug: sits at MINUS the keyed range spot, so the correct call
    // parks it on top of the splash group
    ctx.fillStyle = "#ffcf5c";
    const bx = rx(Math.max(-SPAN, Math.min(SPAN, -ps.range)));
    ctx.beginPath(); ctx.moveTo(bx, y0 - 2); ctx.lineTo(bx - 5, y0 - 10); ctx.lineTo(bx + 5, y0 - 10); ctx.closePath(); ctx.fill();
    // deflection strip
    const dy = 102;
    ctx.fillStyle = "#527257"; ctx.textAlign = "left"; ctx.fillText("DEFLECTION", 8, dy - 8);
    ctx.strokeStyle = "#1d3a28";
    ctx.beginPath(); ctx.moveTo(18, dy); ctx.lineTo(W - 18, dy); ctx.stroke();
    ctx.strokeStyle = "rgba(255,207,92,0.85)";
    ctx.beginPath(); ctx.moveTo(W / 2, dy - 6); ctx.lineTo(W / 2, dy + 6); ctx.stroke();
    ctx.fillStyle = "#8fe3a0";
    ctx.beginPath(); ctx.ellipse(rx(Math.max(-SPAN, Math.min(SPAN, ps.mpiDefl))), dy, 5, 3, 0, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#ffcf5c";
    const dxb = rx(Math.max(-SPAN, Math.min(SPAN, -ps.defl)));
    ctx.beginPath(); ctx.moveTo(dxb, dy - 8); ctx.lineTo(dxb - 5, dy - 15); ctx.lineTo(dxb + 5, dy - 15); ctx.closePath(); ctx.fill();
    ctx.fillStyle = "#cfe0d4"; ctx.font = "11px monospace"; ctx.textAlign = "center";
    ctx.fillText("PARK THE GOLD BUG ON THE SPLASHES — C SENDS THE SPOT", W / 2, H - 6);
  }

  elevToRange(elev) {
    // invert the table roughly
    let lo = 100, hi = MAX_RANGE;
    for (let i = 0; i < 18; i++) {
      const mid = (lo + hi) / 2;
      if (solveElevation(mid).elev < elev) lo = mid; else hi = mid;
    }
    return lo;
  }

  drawRangefinder() {
    const cv = document.getElementById("st-rf-canvas");
    if (!cv) return;
    const ctx = cv.getContext("2d");
    const W = cv.width, H = cv.height;
    ctx.fillStyle = "#0b1a10"; ctx.fillRect(0, 0, W, H);
    // a pending spot call replaces the rangefinder with the Mk 8 spot scope
    if (this.G.fc.pendingSpot) { this.drawSpotScope(ctx, W, H); return; }
    const err = this.rf.knob - this.rf.hidden; // px offset between the halves
    const mid = H / 2;
    // horizon halves
    ctx.strokeStyle = "#3f7a52"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(0, mid - 18); ctx.lineTo(W, mid - 18); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, mid + 18); ctx.lineTo(W, mid + 18); ctx.stroke();
    // target silhouettes: upper image fixed, lower shifted by err (coincidence rangefinder)
    const drawShip = (cx, y, flip) => {
      ctx.save();
      ctx.translate(cx, y);
      if (flip) ctx.scale(1, -1);
      ctx.fillStyle = "#9fd4a8";
      ctx.fillRect(-46, -8, 92, 8);       // hull
      ctx.fillRect(-18, -20, 16, 12);     // superstructure
      ctx.fillRect(6, -26, 5, 18);        // mast
      ctx.fillRect(-34, -16, 8, 8);       // turret
      ctx.restore();
    };
    const hasT = !!this.G.fc.target;
    if (hasT) {
      drawShip(W / 2, mid - 18 + 8, false);
      drawShip(W / 2 + err, mid + 18, true);
    } else {
      ctx.fillStyle = "#9fd4a8"; ctx.font = "13px monospace"; ctx.textAlign = "center";
      ctx.fillText("no target on the wire — designate from CIC or press T on a ship", W / 2, mid);
    }
    // center hairline
    ctx.strokeStyle = "rgba(255,207,92,0.8)";
    ctx.beginPath(); ctx.moveTo(W / 2, 6); ctx.lineTo(W / 2, H - 6); ctx.stroke();
    // alignment quality lamp
    if (hasT) {
      const q = Math.max(0, 1 - Math.abs(err) / 55);
      ctx.fillStyle = q > 0.85 ? "#7dd87d" : q > 0.5 ? "#ffb14e" : "#ff6a5e";
      ctx.beginPath(); ctx.arc(W - 18, 16, 7, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#cfe0d4"; ctx.font = "11px monospace"; ctx.textAlign = "right";
      ctx.fillText("CUT WHEN THE IMAGES KISS", W - 34, 20);
    }
  }
}
